import { isAppHome, deviceService } from '@/services/deviceService';
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlobalStateContext } from '../context/GlobalContextProvider';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import siteIcon from '@assets/site-icon.png';
import { URLs } from '@constants/URLs';
import { gUtils } from '@/utils/gUtils';
import { biz3utils } from '@/utils/biz3utils';

const LoginIndex = () => {
  const navigate = useNavigate();
  const { gAuth, gStripe, setSnackbarValue } = useContext(GlobalStateContext);
  const { t } = useTranslation(); // i18n
  const [btnState, setBtnState] = useState({ ready: false, loading: false });
  const [pagePwd, setPagePwd] = useState('');
  const [loginMail, setMail] = useState('');
  const [isVerifyCode, setVerifying] = useState(false); //判斷顯示輸入mail或顯示輸入密嗎
  const [isSigning, setIsSigning] = useState(false); //circle progress
  const [errHint, setErrHint] = useState(false);
  const [mailChk, setMailChk] = useState(true);
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/';
  const loginBtn = useRef(null);

  const handleAutoLogin = async () => {
    setMail(searchParams.get('email').trim());
    setMailChk(true);
    setBtnState({ ready: true, loading: true });
    if (localStorage.getItem('curLogin')) await gAuth.handleSignout();
    gAuth.handleSign({
      loginMail: searchParams.get('email').trim(),
      cb: (resp) => {
        if (resp instanceof Error) {
          setSnackbarValue({
            logScope: 'pages/login.handleAutoLogin',
            severity: 'error',
            open: true,
            msg: resp.message,
          });
          return;
        }
        setVerifying(true);
        setBtnState({ ready: true, loading: false });
        setTimeout(() => {
          setPagePwd(searchParams.get('code').trim());
        }, 200);
      },
    });
  };

  useEffect(() => {
    if (searchParams.get('email') && searchParams.get('code')) {
      handleAutoLogin();
      return;
    }
    let loginStripe = localStorage.getItem('curLogin');
    if (loginStripe && !isAppHome) {
      navigate(isAppHome ? '/?appHome=1&fromType=app' : '/'); // 如果已经登录，直接跳转到首页
    }
  }, []);

  useEffect(() => {
    if (pagePwd.length === 4) {
      setIsSigning(true);
      // 通过 webSocket 路由 signIn , 调用 lambda Hub3WebSocketAPI_signIn
      // 在 AWS Lambda: Hub3WebSocketAPI_signIn 里：
      // 先调用 cognito signUp 注册，
      // 然后通过 cognito 的 用户池->DevTeamJP->身份验证->扩展->创建身份验证质询 Lambda 触发器-> lambda函数account_create_auth  来发送验证码 到客户邮箱
      gAuth.handleChallenge({
        pagePwd,
        cb: (resp) => {
          setVerifying(false);
          if (resp instanceof Error) {
            setSnackbarValue({ logScope: 'pages/login.LoginIndex', severity: 'error', open: true, msg: resp.message });
            return;
          }
          // App 内登录成功由 native 接管（关闭 webview + 刷新），无需 Web 侧跳转
          if (resp && resp.appLogin) {
            return;
          }
          gStripe.getCustomerInfo(isAppHome ? 'ch_CandyhouseMobile' : loginMail);
          navigate(redirectPath);
        },
      });
    }
  }, [pagePwd]); // eslint-disable-line react-hooks/exhaustive-deps

  // 輸入完帳號時，按下enter等於點擊登入鍵
  const handleKeydown = (e) => {
    if (e.target.value !== '' && e.keyCode === 13) {
      loginBtn.current.click();
    }
  };

  const handleLoginBtnClick = async () => {
    setBtnState({ ready: btnState.ready, loading: true });
    gAuth.handleSign({
      loginMail,
      cb: (resp) => {
        setPagePwd('');
        if (resp instanceof Error) {
          setSnackbarValue({
            logScope: 'pages/login.handleLoginBtnClick',
            severity: 'error',
            open: true,
            msg: resp.message,
          });
          return;
        }
        setVerifying(true);
        setBtnState({ ready: btnState.ready, loading: false });
      },
    });
  };

  const handleOpenExternalUrl = useCallback((event, url) => {
    // Stay in the app: open in the app's web page view, like push links and the shop.
    if (isAppHome) {
      event.preventDefault();
      deviceService
        .request('openWebPage', { url })
        .catch(() => biz3utils.triggerBridge({ action: 'requestOpenExternalURL', url }));
      return;
    }
    const openedByApp = biz3utils.triggerBridge({
      action: 'requestOpenExternalURL',
      url,
    });
    if (openedByApp) {
      event.preventDefault();
    }
  }, []);

  return (
    <Box
      sx={{
        flexDirection: 'column',
        display: 'flex',
        height: '100vh',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          padding: '24px 10px 0 10px',
          maxWidth: '360px',
          margin: 'auto',
        }}
      >
        <Box
          component="img"
          src={siteIcon}
          alt="Biz"
          sx={{
            display: 'block',
            maxHeight: '100px',
            maxWidth: '100%',
            margin: '16px auto',
          }}
        />
        <TextField
          type="email"
          inputMode="email"
          variant="standard"
          fullWidth
          InputProps={{ disableUnderline: true }}
          placeholder={t('pages.login.Email')}
          value={loginMail}
          onKeyDown={(e) => {
            handleKeydown(e);
          }}
          onChange={(e) => {
            setMail(e.target.value);
            const mailChk = gUtils.isValidEmail(e.target.value);
            setBtnState({ ready: mailChk, loading: false });
            setMailChk(mailChk);
          }}
          sx={{
            display: isVerifyCode ? 'none' : 'block',
            my: 1,
            '& .MuiInputBase-root': {
              py: '12px',
              borderBottom: '1px solid lightgray',
            },
            '& .MuiInputBase-input': {
              textAlign: 'center',
              fontSize: '20px',
              p: 0,
              outline: 'none',
            },
            '& .MuiInputBase-root.Mui-focused': {
              borderBottomColor: 'lightgray',
            },
          }}
        />
        <Typography
          variant="h6"
          color="error"
          sx={{
            display: mailChk ? 'none' : 'block',
            textAlign: 'center',
            marginBottom: '30px',
          }}
        >
          {t('pages.login.EmailAddressFormatIsIncorrect')}
        </Typography>

        <Box sx={{ display: isVerifyCode ? 'none' : 'block' }}>
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            <a
              href={URLs.terms}
              target="_blank"
              onClick={(event) => handleOpenExternalUrl(event, URLs.appTerms)}
              style={{
                color: '#28aeb1',
                fontWeight: 'bold',
                display: 'inline',
                cursor: 'pointer',
              }}
              rel="noopener noreferrer"
            >
              {t('pages.login.TermsOfService')}
            </a>
            {t('pages.login.And')}
            <a
              href={URLs.privacy}
              target="_blank"
              onClick={(event) => handleOpenExternalUrl(event, URLs.appPrivacy)}
              style={{
                color: '#28aeb1',
                fontWeight: 'bold',
                display: 'inline',
              }}
              rel="noopener noreferrer"
            >
              {t('pages.login.PrivacyPolicy')}
            </a>
            {t('pages.login.IfYouAgreePleaseLogIn')}
          </Typography>
        </Box>

        <LoadingButton
          ref={loginBtn}
          loading={btnState.loading}
          fullWidth
          sx={{
            color: '#fff',
            marginTop: '16px',
            display: isVerifyCode ? 'none' : 'block',
            '& .MuiLoadingButton-loadingIndicator': {
              top: '50%',
              transform: 'translate(-50%, -50%)',
            },
          }}
          variant="contained"
          color="primary"
          disabled={!btnState.ready}
          onClick={async () => {
            handleLoginBtnClick();
          }}
        >
          {t('pages.login.Login')}
        </LoadingButton>

        <Box style={{ display: isVerifyCode ? 'block' : 'none' }}>
          <CircularProgress
            color="primary"
            sx={{
              display: isSigning ? 'block' : 'none',
              margin: '0 auto',
              mb: 3,
            }}
          />
          <Typography
            variant="h5"
            color="initial"
            sx={{
              display: isVerifyCode ? 'block' : 'none',
              textAlign: 'center',
            }}
          >
            {t('pages.login.EnterVerificationCode')}
          </Typography>

          <TextField
            placeholder="1234"
            type="text"
            inputMode="numeric"
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true }}
            value={pagePwd}
            disabled={!isVerifyCode}
            onChange={(e) => {
              const currentPWD = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPagePwd(currentPWD);
            }}
            sx={{
              my: 1,
              '& .MuiInputBase-root': {
                py: '12px',
                borderBottom: '1px solid lightgray',
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '20px',
                p: 0,
                outline: 'none',
              },
              '& .MuiInputBase-root.Mui-focused': {
                borderBottomColor: 'lightgray',
              },
            }}
          />

          <Typography
            variant="h6"
            color="error"
            sx={{
              display: errHint ? 'block' : 'none',
              textAlign: 'center',
              marginBottom: '30px',
            }}
          >
            入力された認証コードが正しくありません。認証コードが再発行されていますので、新しく受け取ったコードを入力して下さい。
          </Typography>

          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="text"
              onClick={() => {
                setVerifying(false);
                setErrHint(false);
                setMail('');
              }}
              style={{
                color: '#28aeb1',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              {t('pages.login.ReturnToMailInput')}
            </Button>
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <img
          alt="icon"
          style={{ width: '36%' }}
          src="https://cdn.shopify.com/s/files/1/0016/1870/6495/files/busniess.png?v=1663835284"
        />
        <img
          alt="icon"
          style={{ width: '36%' }}
          src="https://cdn.shopify.com/s/files/1/0016/1870/6495/files/girlAndDoor-07.png?v=1663835270"
        />
      </Box>
    </Box>
  );
};

export default LoginIndex;
